import {useRef} from "react";

import {useFastlyQuery} from "@khan/data-gql";
import {RequestInfo} from "@khan/shared-core";
import {useLocation} from "@khan/urls";

import {CONTENT_ROUTE_QUERY} from "../../queries.ts";
import {
    mapGqlCourse,
    mapGqlLearnableContent,
} from "../../types/gql-type-mappers.ts";
import {
    getUnlistedUnitIds,
    prepareContentQueryDataForCacheDispatch,
} from "../../ui/use-content-router.ts";

import type {State} from "./content-cache-types.ts";
import reducer, {initialState} from "./reducer.ts";

type HydratedContentStateProps = {
    listedState: State;
    unlistedState: State;
};

/**
 * Provides content data for the current route if the query has already been
 * fetched, including via SSR. Returned content is in the format to be injected
 * directly in to the content cache, if desired.
 * This will not initiate a fetch of any content data.
 * This will only return the cached value once, if it exists. Additional renders
 * will get back null values.
 */
const useHydratedContentState = ():
    | HydratedContentStateProps
    | null
    | undefined => {
    const location = useLocation();
    // Track if this hook has already run and returned a value.
    const hasRun = useRef(false);
    let contentPath = location.pathname;
    // Clean up the path provided. We want the "relative" content path.
    if (contentPath.charAt(0) === "/") {
        contentPath = contentPath.substring(1);
    }

    // "run" the content query. skip: true ensures it never executes, but we'll
    // get back the SSR hydrated result if it exists. The actual query is run in
    // useContentRouter a few layers down the stack inside of the ContentCache
    // contexts.
    const pathQueryResult = useFastlyQuery(CONTENT_ROUTE_QUERY, {
        skip: true,
        variables: {
            path: contentPath,
            countryCode: RequestInfo.remoteCountry,
        },
    });

    if (hasRun.current) {
        return null;
    }

    // Flip the hasRun flag on the first run through this hook.
    // The effects of this useEffect will prevent subsequent renders of this
    // hook from proceeding, but the first render will continue to execute
    // and will return cached data if it exists.
    // We do this to avoid running the `reducer()` function below. It is extra
    // processing of a large object that would happen on all cached page
    // renders without this prevention measure.
    if (!hasRun.current) {
        hasRun.current = true;
    }

    if (pathQueryResult.status === "success") {
        const data = pathQueryResult.data;

        const {
            courseData,
            learnableContentData,
            unlisted,
            publishedContentVersion,
        } = prepareContentQueryDataForCacheDispatch(data);

        if (courseData) {
            // If we're here, this means that this is our first clientside
            // render, and we found cached data from the serverside render that
            // we want to use.
            // Run the reducer outside of dispatch. We want the cache that would
            // be generated from the update, to initialize the cache with.
            // The cache needs to be initialized with the data because
            // dispatching requires a second render for the cache data to be
            // consumed. That would cause the client side first render not to
            // match the SSR'd result (server=full page, client=loading).
            const stateData = reducer(
                initialState,
                learnableContentData
                    ? {
                          unlisted,
                          publishedContentVersion,
                          type: "SET_COURSE_AND_CONTENT",
                          data: {
                              course: mapGqlCourse(courseData),
                              unlistedUnitIds: getUnlistedUnitIds(courseData),
                              content:
                                  mapGqlLearnableContent(learnableContentData),
                          },
                      }
                    : {
                          unlisted,
                          publishedContentVersion,
                          type: "SET_COURSE",
                          data: {
                              course: mapGqlCourse(courseData),
                              unlistedUnitIds: getUnlistedUnitIds(courseData),
                          },
                      },
            );

            // For each cache state, return the appropriate data or the initial
            // state to set up the cache.
            const listedState = unlisted ? initialState : stateData;
            const unlistedState = unlisted ? stateData : initialState;

            return {
                listedState,
                unlistedState,
            };
        }
    }

    return null;
};

export default useHydratedContentState;
